import { getTimeSign } from '@/api/public';
import { GiftDialogFlow } from '@/components/GiftDetailsDialog';
import GiftListDialog from '@/components/GiftListDialog';
import type { CartShowGiftDto } from '@/api/types/gift';
import type { CartSummaryGiftExposureViewModel } from '@/domain/cart/gift-exposure';
import { useCartGiftTracking } from '@/domain/cart/gift-tracking/client';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import { Badge } from '@mui/material';
import { ShoppingBagOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import GiftPopover from './GiftPopover';
import HeaderHoverPopover from './HeaderHoverPopover';
import { useWhenCurReady } from '@/hooks/useWhenReady';
const CartPopover = dynamic(() => import('./CartPopover'), { ssr: false });

export default function CartIcon({
  isDarkMode,
  cartProductNum,
}: {
  isDarkMode: boolean;
  cartProductNum: number | string;
}) {
  const { trackEvent } = useReporting();
  const { pathname, cnName } = useRouteMetadata();
  const router = useRouter();
  const giftCount = useStore((state) => state.giftCount);
  const [showCartPopover, setShowCartPopover] = useState(false);
  const [, setCountDownTime] = useState(5);
  const showGiftPopoverTimer = useRef<NodeJS.Timeout | null>(null);
  const setCart = useStore((state) => state.setCart);
  const isMobile = useStore((state) => state.isMobileScreen);
  const [cartPopoverShow, setCartPopoverShow] = useState(false);
  const [showTimer, setShowTimer] = useState<NodeJS.Timeout | null>(null);
  const [cartGiftDialog, setCartGiftDialog] = useState<CartSummaryGiftExposureViewModel | null>(
    null,
  );
  const [cartGiftShowGift, setCartGiftShowGift] = useState<CartShowGiftDto | null>(null);
  const currency = useStore((state) => state.currency);
  const giftTracking = useCartGiftTracking({
    showGift: cartGiftShowGift || undefined,
    currency: currency?.name || '',
    open: Boolean(cartGiftDialog),
  });
  // Store state
  const canBuyCartsList = useStore((state) => state.canBuyCartsList);
  const giftPopoverShowInterval = useRef<NodeJS.Timeout | null>(null);
  const [cartPreviewOpen, setCartPreviewOpen] = useState(false);
  const tA11y = useTranslations('layout.header.a11y');
  const giftT = useTranslations('cart.GiftsWithCartItem');

  // 图标颜色类名
  const iconColorClass = isDarkMode ? 'text-white' : 'text-primary-black';

  // Badge公共样式
  const commonBadgeStyles = {
    position: 'absolute',
    boxSizing: 'border-box',
    textAlign: 'center',
    color: '#fff',
    backgroundColor: colors.primaryPurple,
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: font.br,
    display: 'flex',
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'auto',
    '& .MuiBadge-badge.MuiBadge-standard.MuiBadge-invisible': {
      display: 'none',
    },
  };

  // Badge样式配置 - 根据设备类型选择特定样式
  const deviceSpecificStyles = isMobile
    ? {
        // 移动端特定样式
        top: '-1.067vw',
        left: '3.733vw',
        minWidth: '3.733vw',
        height: '3.733vw',
        lineHeight: '3.733vw',
        padding: '0 1.067vw',
        borderRadius: '2.133vw',
        fontSize: '2.933vw',
      }
    : {
        // PC端特定样式
        top: '-8px',
        left: '39px',
        minWidth: '18px',
        height: '18px',
        lineHeight: '18px',
        padding: '0 5px',
        borderRadius: '10px',
        fontSize: '12px',
      };

  // 合并公共样式和设备特定样式
  const badgeStyles = {
    ...commonBadgeStyles,
    ...deviceSpecificStyles,
  };

  useWhenCurReady(() => {
    // 初始化购物车的数据
    setCart();

    return () => {
      if (showGiftPopoverTimer.current) {
        clearInterval(showGiftPopoverTimer.current);
      }
    };
  });

  const handleCartClick = () => {
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '购物车',
          url_path: window.location.href,
          page_name: cnName, // 使用路由元数据中的中文名称
          tab_index: 4,
        },
      },
      [ProviderType.SENSORS],
    );
    sessionStorage.setItem('zl-sensors-entrance-cart-scene', '导航位');
    if (pathname === '/cart') {
      return;
    }
    router.push('/cart');
  };

  const judgeGiftPopoverShow = useCallback(() => {
    const flag1 = giftCount > 0; // 赠品的数量大于0
    const flag2 = !['/goods-detail', '/goods-list', '/cart'].some((path) =>
      location.pathname?.startsWith(path),
    );
    if (flag1 && flag2) {
      setShowCartPopover(true);
    }
  }, [giftCount]);

  useEffect(() => {
    judgeGiftPopoverShow();
    giftPopoverShowInterval.current && clearInterval(giftPopoverShowInterval.current);
    giftPopoverShowInterval.current = setInterval(
      () => {
        judgeGiftPopoverShow();
      },
      15 * 60 * 1000,
    );
  }, [giftCount]);

  // 监听礼品弹窗状态变化
  useEffect(() => {
    if (showCartPopover) {
      if (showGiftPopoverTimer.current) {
        clearInterval(showGiftPopoverTimer.current);
      }
      showGiftPopoverTimer.current = setInterval(() => {
        setCountDownTime((prev) => {
          if (prev <= 1) {
            setShowCartPopover(false);
            return 5;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCountDownTime(5);
      if (showGiftPopoverTimer.current) {
        clearInterval(showGiftPopoverTimer.current);
      }
    }
  }, [showCartPopover]);

  const getServerTimeSign = async () => {
    const { data: { exist } = {} } = await getTimeSign({
      type: 'todayCartView',
      second: 3,
    });
    return exist;
  };
  // Watch cartDown prop
  useEffect(() => {
    if (cartPopoverShow) {
      // 弹层弹出3s，视为访问过购物车页
      const timer = setTimeout(() => {
        getServerTimeSign();
      }, 3000);
      setShowTimer(timer);
      trackEvent(
        {
          eventName: 'CartPopUp_Exp',
          properties: {
            page_name: cnName,
            url_path: window.location.href,
          },
        },
        [ProviderType.SENSORS],
      );
    } else if (showTimer) {
      clearTimeout(showTimer);
      setShowTimer(null);
    }

    return () => {
      if (showTimer) {
        clearTimeout(showTimer);
      }
    };
  }, [cartPopoverShow]);

  const cartButtonClass = isMobile
    ? 'flex justify-center items-center p-0 m-0 border-0 bg-transparent cursor-pointer pb-[14px] -mb-[14px]'
    : 'flex justify-center items-center p-0 m-0 border-0 bg-transparent cursor-pointer pb-[14px] -mb-[14px] pl-[24px]';

  return (
    <>
      <li
        className={
          isMobile
            ? 'flex justify-center items-center relative h-[6.400vw] w-[6.400vw] mr-[5.333vw]'
            : 'relative'
        }
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
        }}
        onClick={handleCartClick}
      >
        {pathname === '/cart' || isMobile || canBuyCartsList.length === 0 ? (
          <button
            type="button"
            aria-label={tA11y('cart')}
            onClick={(e) => {
              e.stopPropagation();
              handleCartClick();
            }}
            className={cartButtonClass}
          >
            <ShoppingBagOutlined
              className={`text-[24px] ${iconColorClass} cursor-pointer`}
              aria-hidden
            />
          </button>
        ) : (
          <HeaderHoverPopover
            id="cart-popover"
            onOpenChange={setCartPreviewOpen}
            triggerComponent={
              <button
                type="button"
                aria-label={tA11y('openCartPreview')}
                aria-expanded={cartPreviewOpen}
                aria-haspopup="dialog"
                className={cartButtonClass}
                onClick={handleCartClick}
              >
                <ShoppingBagOutlined
                  className={`text-[24px] ${iconColorClass} cursor-pointer`}
                  aria-hidden
                />
              </button>
            }
            content={
              <CartPopover
                onGiftSummaryClick={(gift, showGift) => {
                  setCartGiftShowGift(showGift);
                  setCartGiftDialog(gift);
                }}
              />
            }
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            width={'24.062vw'}
            noPadding
            slotProps={{
              transition: {
                onEnter: () => {
                  setCartPopoverShow(true);
                },
                onExited: () => {
                  setCartPopoverShow(false);
                },
              },
            }}
          />
        )}
        {!!cartProductNum && (
          <Badge sx={badgeStyles}>
            <span
              style={{
                ...(cartProductNum === '...' && { transform: 'translateY(-20%)' }),
              }}
            >
              {cartProductNum}
            </span>
          </Badge>
        )}
        <GiftPopover show={showCartPopover} giftCount={giftCount} />
      </li>
      {cartGiftDialog?.dialogType === 'tier-flow' && cartGiftDialog.dialogActivities?.length ? (
        <GiftDialogFlow
          open={Boolean(cartGiftDialog)}
          onClose={() => {
            setCartGiftDialog(null);
            setCartGiftShowGift(null);
          }}
          title={cartGiftDialog.dialogTitle || giftT('giftList')}
          activities={cartGiftDialog.dialogActivities}
        />
      ) : null}
      {cartGiftDialog?.dialogType === 'gift-list' && cartGiftDialog.giftListItems?.length ? (
        <GiftListDialog
          open={Boolean(cartGiftDialog)}
          title={cartGiftDialog.dialogTitle || giftT('giftList')}
          items={cartGiftDialog.giftListItems}
          onClose={() => {
            setCartGiftDialog(null);
            setCartGiftShowGift(null);
          }}
        />
      ) : null}
    </>
  );
}
